import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import type { SyncStatus } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { AutoSyncControls } from "@/components/shared/AutoSyncControls";

interface Props {
  accountId: string;
  autoStart?: boolean;
}

/** Compact sync widget rendered under each OAuth-connected YouTube account. */
export const SyncControls: React.FC<Props> = ({ accountId, autoStart = false }) => {
  const qc = useQueryClient();
  const [pollFast, setPollFast] = useState(false);
  const [autoStartFired, setAutoStartFired] = useState(false);

  const status = useQuery<SyncStatus>({
    queryKey: ["sync-status", accountId],
    queryFn: async () =>
      (await api.get(`/platforms/${accountId}/sync/status`)).data,
    refetchInterval: pollFast ? 2500 : false,
  });

  const running =
    status.data?.job?.status === "queued" || status.data?.job?.status === "running";

  useEffect(() => {
    if (running && !pollFast) setPollFast(true);
    if (!running && pollFast) {
      setPollFast(false);
      if (status.data?.job?.status === "completed") {
        // Refresh downstream data
        qc.invalidateQueries({ queryKey: ["dashboard-summary"] });
        qc.invalidateQueries({ queryKey: ["comments"] });
        qc.invalidateQueries({ queryKey: ["leads"] });
        qc.invalidateQueries({ queryKey: ["complaints"] });
        qc.invalidateQueries({ queryKey: ["platforms"] });
        qc.invalidateQueries({ queryKey: ["synced-posts", accountId] });
        qc.invalidateQueries({ queryKey: ["sync-suggestions", accountId] });
      }
    }
  }, [running, pollFast, status.data?.job?.status, qc, accountId]);

  const startSync = useMutation({
    mutationFn: async () =>
      (await api.post(`/platforms/${accountId}/sync`)).data,
    onSuccess: (d) => {
      toast.info(d.message || "Sync started");
      setPollFast(true);
      qc.invalidateQueries({ queryKey: ["sync-status", accountId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Could not start sync"),
  });

  // Fire auto-start once after the account is first OAuth-connected
  useEffect(() => {
    if (autoStart && !autoStartFired && status.data && !running && !status.data.last_synced_at) {
      setAutoStartFired(true);
      startSync.mutate();
    }
  }, [autoStart, autoStartFired, status.data, running, startSync]);

  const job = status.data?.job;
  const pct = job && job.videos_total > 0
    ? Math.min(100, Math.round((job.videos_synced / job.videos_total) * 100))
    : 0;

  return (
    <div
      className="mt-2 rounded-md border border-[color:var(--sg-border)] bg-slate-50/60 p-3"
      data-testid={`sync-controls-${accountId}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <RefreshCw className={`h-4 w-4 text-[color:var(--sg-fg)] ${running ? "animate-spin" : ""}`} />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">
              {running
                ? "Sync in progress"
                : status.data?.last_synced_at
                ? "Channel synced"
                : "Channel not synced yet"}
            </div>
            <div className="text-xs text-[color:var(--sg-muted)] truncate">
              {running
                ? job?.message || "Fetching data…"
                : status.data?.last_synced_at
                ? `Last synced ${new Date(status.data.last_synced_at).toLocaleString()} · ${status.data.videos_count} videos indexed`
                : "Pull existing videos, comments, likes and engagement into SociBaba."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            data-testid={`sync-now-${accountId}`}
            disabled={running || startSync.isPending}
            onClick={() => startSync.mutate()}
            className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white h-8 text-xs"
          >
            {running || startSync.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
            )}
            {status.data?.last_synced_at ? "Re-sync" : "Sync now"}
          </Button>
          {(status.data?.videos_count ?? 0) > 0 && (
            <Link to={`/platforms/${accountId}/insights`}>
              <Button
                size="sm"
                variant="ghost"
                data-testid={`open-channel-insights-${accountId}`}
                className="h-8 text-xs"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1" /> Insights
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </div>

      {running && (
        <div className="mt-2">
          <div className="h-1.5 w-full bg-slate-200 rounded overflow-hidden">
            <div
              className="h-full bg-[color:var(--sg-accent,#7C3AED)] transition-all"
              style={{ width: `${pct}%` }}
              data-testid={`sync-progress-${accountId}`}
            />
          </div>
          <div className="mt-1 text-[11px] text-[color:var(--sg-muted)]">
            {job ? (
              <>
                {job.videos_synced}/{job.videos_total || "?"} videos ·{" "}
                {job.comments_synced} comments classified ·{" "}
                {job.leads_derived} leads · {job.complaints_derived} complaints
              </>
            ) : (
              "Starting…"
            )}
          </div>
        </div>
      )}

      {job?.status === "completed" && !running && (
        <div className="mt-2 space-y-1">
          <div className="text-[11px] text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {job.message}
          </div>
          {job.error && (
            <div
              className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              data-testid={`sync-warning-${accountId}`}
            >
              {job.error}
            </div>
          )}
          {job.debug_first && (
            <details className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <summary className="cursor-pointer text-slate-600">
                First-video diagnostic (click to expand)
              </summary>
              <pre className="mt-2 overflow-auto text-[10px] leading-relaxed"
                    data-testid={`sync-debug-${accountId}`}>
{JSON.stringify(job.debug_first, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}

      {job?.status === "failed" && (
        <div className="mt-2 text-[11px] text-[color:var(--sg-danger,#dc2626)]" data-testid={`sync-error-${accountId}`}>
          {job.error || job.message}
        </div>
      )}

      <AutoSyncControls accountId={accountId} />
    </div>
  );
};
